import { useMemo } from 'react';
import { itemHeight } from '../../palette/rows';

export const OVERSCAN = 6;

/**
 * Fixed-height windowing (PLAN D7): known heights per item type, prefix sums,
 * a binary search for the first visible item, `overscan` items either side.
 * Pure math in `computeWindow` (tested); the hook only memoises it.
 */
export function computeOffsets(items) {
  const offsets = new Array(items.length + 1);
  offsets[0] = 0;
  for (let i = 0; i < items.length; i += 1) offsets[i + 1] = offsets[i] + itemHeight(items[i]);
  return offsets;
}

export function computeWindow(offsets, scrollTop, viewport, overscan = OVERSCAN) {
  const n = offsets.length - 1;
  if (n <= 0) return { start: 0, end: 0, total: 0 };
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsets[mid] <= scrollTop) lo = mid;
    else hi = mid - 1;
  }
  const first = lo;
  let last = first;
  const bottom = scrollTop + viewport;
  while (last < n - 1 && offsets[last + 1] < bottom) last += 1;
  return { start: Math.max(0, first - overscan), end: Math.min(n, last + 1 + overscan), total: offsets[n] };
}

export default function usePaletteWindow(items, scrollTop, viewport) {
  const offsets = useMemo(() => computeOffsets(items), [items]);
  const win = useMemo(() => computeWindow(offsets, scrollTop, viewport), [offsets, scrollTop, viewport]);
  return { ...win, offsets };
}
